'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo, useRef } from 'react';

/**
 * Guards against out-of-order resolution of concurrent async requests so that
 * only the most recently started request may apply its result. This is the
 * canonical "latest-request-wins" / stale-response pattern (see
 * `.claude/rules/concurrency.md` § 2): call `begin()` when an async operation
 * starts, then check `isCurrent(id)` before applying its result; a superseded
 * request — whether it resolves or rejects — is discarded.
 *
 * The counter is backed by a `useRef`, so ids are monotonic across renders and
 * effect re-runs. This guard does NOT handle unmount/cancellation — pair it
 * with a `cancelled` flag or `mountedRef` when the component can unmount mid-flight.
 */
export interface LatestRequestGuard {
  /** Start a new request; returns its monotonic id and supersedes all prior ids. */
  begin: () => number;
  /** True only when `id` is the most recently begun request (not yet superseded). */
  isCurrent: (id: number) => boolean;
}

/** Creates a stable latest-request guard whose counter persists across re-renders. */
export function useLatestRequestGuard() {
  const $ = _c(3);
  const latestId = useRef(0);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => latestId.current = latestId.current + 1;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const begin = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = id => latestId.current === id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isCurrent = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      begin,
      isCurrent
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}